import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  IconButton,
  TextField,
  Button,
  Avatar,
  Chip,
  Tooltip,
} from "@mui/material";
import { Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";

/**
 * props:
 * - open: boolean
 * - onClose: () => void
 * - product: {
 *     id, name, sku, image,
 *     variations: [{ color, sizes: [{ size, stock, price, status, variantId? }] }]
 *   }
 * - onConfirm: ({ color, size, price, stock, quantity, variantId }) => void
 */
export default function VariantPickerDialog({ open, onClose, product, onConfirm }) {
  // Helpers
  const isSizeAvailable = (s) =>
    (s?.stock ?? 0) > 0 && (s?.status === undefined || s?.status === "Y" || s?.status === true);

  const availableColors = useMemo(() => {
    const v = product?.variations || [];
    return v
      .map((x) => ({
        color: x.color,
        hasAvailable: (x.sizes || []).some(isSizeAvailable),
      }))
      .filter((c) => c.color);
  }, [product]);

  const [color, setColor] = useState("");
  const sizesOfColor = useMemo(() => {
    const found = (product?.variations || []).find((v) => v.color === color);
    return found?.sizes || [];
  }, [product, color]);

  const [size, setSize] = useState("");
  const selectedSize = sizesOfColor.find((s) => s.size === size);
  // const price = selectedSize?.price ?? product?.finalPrice ?? 0;
  const price =
  selectedSize?.finalPrice ?? product?.finalPrice ?? selectedSize?.price ??
  
  product?.price ??
  0;

  const stock = selectedSize?.stock ?? 0;

  const [quantity, setQuantity] = useState(1);

  // Chọn mặc định khi mở
  useEffect(() => {
    if (!open || !product) return;

    // chọn màu đầu tiên có size khả dụng
    const firstColor = availableColors.find((c) => c.hasAvailable)?.color || availableColors?.[0]?.color || "";
    setColor(firstColor);

    // sau khi có màu, chọn size đầu tiên còn hàng
    const sizes = (product?.variations || []).find((v) => v.color === firstColor)?.sizes || [];
    const firstSize = sizes.find(isSizeAvailable)?.size || sizes?.[0]?.size || "";
    setSize(firstSize);

    setQuantity(1);
  }, [open, product, availableColors]);

  // Clamp quantity theo stock
  useEffect(() => {
    if (quantity > stock) setQuantity(stock > 0 ? stock : 1);
    if (quantity < 1) setQuantity(1);
  }, [stock, quantity]);

  const handleChangeColor = (_e, v) => {
    if (!v) return;
    setColor(v);
    // reset size theo màu mới
    const sizes = (product?.variations || []).find((vv) => vv.color === v)?.sizes || [];
    const first = sizes.find(isSizeAvailable)?.size || sizes?.[0]?.size || "";
    setSize(first);
    setQuantity(1);
  };

  const handleChangeSize = (_e, v) => {
    if (!v) return;
    setSize(v);
    setQuantity(1);
  };

  const inc = () => setQuantity((q) => Math.min(q + 1, stock || 1));
  const dec = () => setQuantity((q) => Math.max(1, q - 1));

  const handleConfirm = () => {
    if (!color || !size || stock <= 0) return;
    onConfirm({
      color,
      size,
      price,
      stock,
      quantity,
      variantId: selectedSize?.variantId, // nếu backend có
    });
  };

  if (!product) return null;

  // swatch màu cơ bản (nếu chuỗi màu hợp lệ)
  const cssColor = (c) => {
    if (!c) return null;
    const common = {
      black: "#000",
      white: "#fff",
      red: "#f44336",
      blue: "#2196f3",
      green: "#4caf50",
      yellow: "#ffeb3b",
      orange: "#ff9800",
      purple: "#9c27b0",
      gray: "#9e9e9e",
      grey: "#9e9e9e",
      silver: "#c0c0c0",
      gold: "#d4af37",
      brown: "#795548",
      pink: "#e91e63",
      navy: "#001f3f",
      beige: "#f5f5dc",
    };
    const key = c.trim().toLowerCase();
    if (common[key]) return common[key];
    // hex
    if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(c)) return c;
    return null;
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Chọn biến thể</DialogTitle>

      <DialogContent dividers>
        {/* Header sản phẩm */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Avatar
            src={product.image}
            variant="rounded"
            sx={{ width: 56, height: 56, mr: 2 }}
          >
            {product.name?.[0]}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600} noWrap>
              {product.name}
            </Typography>
            {!!product.sku && (
              <Typography variant="body2" color="text.secondary">
                SKU: {product.sku}
              </Typography>
            )}
          </Box>

          <Box sx={{ textAlign: "right" }}>
            <Typography variant="body2" color="text.secondary">
              Giá biến thể
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {price.toLocaleString("vi-VN")}₫
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Chọn màu */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Màu sắc
          </Typography>
          <ToggleButtonGroup
            exclusive
            value={color}
            onChange={handleChangeColor}
            size="small"
            sx={{ flexWrap: "wrap", gap: 1 }}
          >
            {availableColors.map((c) => {
              const sw = cssColor(c.color);
              const disabled = !c.hasAvailable;
              const btn = (
                <ToggleButton
                  key={c.color}
                  value={c.color}
                  disabled={disabled}
                  sx={{ px: 1.5, py: 0.75, borderRadius: 2, textTransform: "none" }}
                >
                  {sw ? (
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        mr: 1,
                        border: "1px solid rgba(0,0,0,0.2)",
                        backgroundColor: sw,
                      }}
                    />
                  ) : null}
                  {c.color}
                </ToggleButton>
              );
              return disabled ? (
                <Tooltip key={c.color} title="Không có size còn hàng">
                  <span>{btn}</span>
                </Tooltip>
              ) : (
                btn
              );
            })}
          </ToggleButtonGroup>
        </Box>

        {/* Chọn size */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Size
          </Typography>
          <ToggleButtonGroup
            exclusive
            value={size}
            onChange={handleChangeSize}
            size="small"
            sx={{ flexWrap: "wrap", gap: 1 }}
          >
            {sizesOfColor.map((s) => {
              const disabled = !isSizeAvailable(s);
              const label = s.size;
              const btn = (
                <ToggleButton
                  key={label}
                  value={label}
                  disabled={disabled}
                  sx={{ px: 1.5, py: 0.75, borderRadius: 2, textTransform: "none" }}
                >
                  {label}
                </ToggleButton>
              );
              return disabled ? (
                <Tooltip key={label} title="Hết hàng hoặc tắt bán">
                  <span>{btn}</span>
                </Tooltip>
              ) : (
                btn
              );
            })}
          </ToggleButtonGroup>
        </Box>

        {/* Tồn kho + số lượng */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
          <Chip
            label={`Còn: ${stock}`}
            size="small"
            color={stock > 0 ? "success" : "default"}
            variant="outlined"
          />

          <Box sx={{ display: "flex", alignItems: "center", ml: "auto" }}>
            <IconButton onClick={dec} disabled={stock <= 0 || quantity <= 1} size="small">
              <RemoveIcon />
            </IconButton>
            <TextField
              value={quantity}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (Number.isNaN(v)) return;
                setQuantity(Math.max(1, Math.min(v, stock || 1)));
              }}
              type="number"
              size="small"
              inputProps={{
                min: 1,
                max: stock || 1,
                style: { textAlign: "center", width: 64 },
              }}
            />
            <IconButton onClick={inc} disabled={stock <= 0 || quantity >= stock} size="small">
              <AddIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Tổng tiền của biến thể * số lượng */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            Tạm tính:
          </Typography>
          <Typography variant="h6" fontWeight={700}>
            {(price * (quantity || 0)).toLocaleString("vi-VN")}₫
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Hủy</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!color || !size || stock <= 0}
        >
          Thêm vào giỏ
        </Button>
      </DialogActions>
    </Dialog>
  );
}
